import { StatusBadge } from '@/components/ui/StatusBadge';
import type { SubmissionStatus } from '@/types/api/submission-status';

export interface Message {
  id: number;
  referenceCode: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  submittedAt: string;
  status: SubmissionStatus;
  handledBy: string | null;
  handledAt: string | null;
}

const SUBJECT_LABELS: Record<string, string> = {
  geral: 'Geral',
  servicos: 'Serviços',
  documentacao: 'Documentação',
  reclamacao: 'Reclamação',
  outro: 'Outro',
};

const HEADERS = ['Referência', 'Nome', 'Email', 'Assunto', 'Data', 'Estado', 'Tratado por'];

interface MessagesTableProps {
  messages: Message[];
  onRowClick: (message: Message) => void;
}

export function MessagesTable({ messages, onRowClick }: MessagesTableProps) {
  if (messages.length === 0) {
    return (
      <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
        <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">inbox</span>
        Nenhuma mensagem encontrada.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-left">
        <thead>
          <tr className="border-line border-b">
            {HEADERS.map((h) => (
              <th
                key={h}
                className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {messages.map((msg) => (
            <tr
              key={msg.id}
              onClick={() => onRowClick(msg)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(msg);
                }
              }}
              tabIndex={0}
              aria-label={`Abrir mensagem de ${msg.name}`}
              className="border-line hover:bg-background focus:bg-background cursor-pointer border-b transition-colors last:border-b-0 focus:outline-none"
            >
              <td className="text-primary px-4 py-3.5 text-xs font-bold tracking-wide">
                {msg.referenceCode}
              </td>
              <td className="text-on-surface px-4 py-3.5 text-sm font-semibold">{msg.name}</td>
              <td className="text-on-surface-light px-4 py-3.5 text-sm">{msg.email}</td>
              <td className="px-4 py-3.5">
                <span className="border-line bg-background text-on-surface-light inline-flex rounded-full border px-2.5 py-0.5 text-xs">
                  {SUBJECT_LABELS[msg.subject] ?? msg.subject}
                </span>
              </td>
              <td className="text-on-surface-light px-4 py-3.5 text-xs">
                {new Date(msg.submittedAt).toLocaleDateString('pt-PT')}
              </td>
              <td className="px-4 py-3.5">
                <StatusBadge status={msg.status} />
              </td>
              <td className="text-on-surface-light px-4 py-3.5 text-xs">{msg.handledBy ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
